import { useCallback, useEffect, useState } from 'react';

import { type Fragment, Slice, type Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { type EditorView } from '@atlaskit/editor-prosemirror/view';

import { type Positions } from '../../../ui/utils/xstate/get-ai-experience-service';
import { createUpdateEndWidgetDecorationPositionCommand } from '../../decoration/actions';
import { inEditorDirectStreamingPluginKey } from '../in-editor-direct-streaming-plugin-key';

function getLastContentDepth(fragment: Fragment) {
	let depth = 0;
	let currentNode: Fragment | PMNode = fragment;

	while (currentNode.lastChild) {
		depth++;
		currentNode = currentNode.lastChild;
	}

	return depth;
}
export const useInEditorDirectStreaming = ({
	view,
	positions,
	replaceDocOnEmptySelection,
}: {
	view: EditorView;
	positions: Positions;
	replaceDocOnEmptySelection?: boolean;
}) => {
	const [isPaused, setIsPaused] = useState(true);
	useEffect(() => {
		const min = 80,
			max = 240;
		const prestreamLoading = setInterval(
			() => {
				if (isPaused) {
					return;
				}
				view.dispatch(
					view.state.tr.setMeta(inEditorDirectStreamingPluginKey, {
						action: 'updateLoadingPos',
					}),
				);
				if (
					(inEditorDirectStreamingPluginKey.getState(view.state) || {}).aiContentEndPos !==
					undefined
				) {
					clearInterval(prestreamLoading);
				}
			},
			Math.random() * (max - min) + min,
		);

		return () => {
			clearInterval(prestreamLoading);
		};
	}, [view, isPaused]);

	const shouldReplaceDoc = replaceDocOnEmptySelection && positions[0] === positions[1];

	const [start, end] = shouldReplaceDoc ? [1, view.state.doc.content.size - 1] : positions;

	const mountDecorations = useCallback(() => {
		setIsPaused(false);
		const pluginState = inEditorDirectStreamingPluginKey.getState(view.state) || {};
		if (pluginState.startPos === undefined) {
			view.dispatch(
				view.state.tr.setMeta(inEditorDirectStreamingPluginKey, {
					action: 'initPositions',
					positions: {
						startPos: start,
						userContentEndPos: end,
					},
				}),
			);
			if (shouldReplaceDoc) {
				createUpdateEndWidgetDecorationPositionCommand(view.state, view.state.doc.content.size - 1)(
					view.state,
					view.dispatch,
				);
			}
		}
		return () => {
			setIsPaused(true);
		};
	}, [view, start, end, shouldReplaceDoc]);

	const updateStream = useCallback(
		(frag: Fragment, isFinalStream = false) => {
			const pluginState = inEditorDirectStreamingPluginKey.getState(view.state);
			const lastFragmentDepth = getLastContentDepth(frag);
			// /** TODO: Revisit slice depth logic - logic based on
			//  *  platform/packages/editor/editor-plugin-ai/src/ui/prebuilt/config-items/utils/action-utils.ts
			//  */
			let slice = new Slice(frag, 1, lastFragmentDepth);
			// /**
			//  * Revisit later - occasionally we receive a response with empty content which causes the fragment -> slice
			//  * to have a negative size, which causes the experience to break.
			//  * At the moment, we can fallback to empty slice but user clicking 'replace' won't see any changes.
			//  */
			if (slice.size < 0) {
				slice = Slice.empty;
			}

			const { startPos, aiContentEndPos, userContentEndPos } = pluginState || {};
			if (startPos === undefined || userContentEndPos === undefined) {
				return;
			}

			const toPos = isFinalStream
				? userContentEndPos
				: Math.min(Math.max(aiContentEndPos || 0, startPos + slice.size + 1), userContentEndPos);

			const tr = view.state.tr.replace(startPos, toPos, slice);

			view.dispatch(
				tr.setMeta(inEditorDirectStreamingPluginKey, {
					action: 'updateStream',
					positions: {
						aiContentEndPos: toPos,
						userContentEndPos: userContentEndPos,
					},
					isFinalStream,
				}),
			);
		},
		[view],
	);

	return {
		mountDecorations,
		updateStream,
	};
};
